import { useCallback, useEffect, useRef, useState } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getTicket } from "../../../service/comms/supportService";

/**
 * Load a single support ticket's detail (participants, csat, escalations, and
 * client context) for a room id. Degrades to `null` when ms-agents is
 * unreachable so the console can still render empty states.
 *
 * @param {string} [roomId]
 * @returns {{
 *   detail: Object|null,
 *   loading: boolean,
 *   error: string|null,
 *   reload: () => void,
 *   applyLocal: (partial: Object) => void,
 * }}
 */
export function useTicketDetail(roomId) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Optimistic overlay from applyLocal, re-applied on top of every fetch for
  // this room — the ms-agents read model can lag a mutation, so a refetch
  // landing right after a pick-up must not flip the header back to
  // "unassigned". Once the backend catches up the merge is a no-op.
  const overlayRef = useRef(null);
  // Drop stale responses when the agent switches tickets quickly.
  const loadSeqRef = useRef(0);

  useEffect(() => {
    overlayRef.current = null;
  }, [roomId]);

  const withOverlay = useCallback((data) => {
    const overlay = overlayRef.current;
    if (!overlay || !data) {
      return data;
    }
    if (data.ticket && typeof data.ticket === "object") {
      return { ...data, ticket: { ...data.ticket, ...overlay } };
    }
    return { ...data, ...overlay };
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    if (!roomId) {
      setDetail(null);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getTicket(commsGet, roomId);
      if (seq !== loadSeqRef.current) {
        return;
      }
      setDetail(withOverlay(data) || null);
      setError(null);
    } catch (err) {
      if (seq !== loadSeqRef.current) {
        return;
      }
      setDetail(null);
      setError(err?.message || "Unable to load ticket");
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [roomId, withOverlay]);

  useEffect(() => {
    load();
  }, [load]);

  // Merge a partial ticket update into the current detail without waiting on
  // a refetch — optimistic action feedback (e.g. pick-up flips the header to
  // "assigned" even when the backend read model lags). Handles both flat and
  // nested (`{ticket: {...}}`) detail shapes; the partial is also kept as an
  // overlay so subsequent (possibly stale) refetches can't undo it.
  const applyLocal = useCallback((partial) => {
    if (!partial || typeof partial !== "object") {
      return;
    }
    overlayRef.current = { ...(overlayRef.current || {}), ...partial };
    setDetail((prev) => {
      if (!prev) {
        return partial;
      }
      if (prev.ticket && typeof prev.ticket === "object") {
        return { ...prev, ticket: { ...prev.ticket, ...partial } };
      }
      return { ...prev, ...partial };
    });
  }, []);

  return { detail, loading, error, reload: load, applyLocal };
}
